import { useEffect, useState } from "react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { StatusBadge } from "../components/ui/StatusBadge";
import { authService, type AccountProfile } from "../services/authService";

export function AccountPage() {
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    authService.loadProfile()
      .then(nextProfile => {
        if (!cancelled) {
          setProfile(nextProfile);
          setError(null);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (isLoading) {
    return <Card className="p-8"><p className="text-xs font-black uppercase tracking-[.28em] text-slate-500">Account</p><h1 className="mt-3 text-4xl font-black">Loading user profile…</h1></Card>;
  }

  if (error || !profile) {
    return (
      <Card className="p-8">
        <p className="text-xs font-black uppercase tracking-[.28em] text-slate-500">Account</p>
        <h1 className="mt-3 text-4xl font-black">Sign in required</h1>
        <p className="mt-4 text-slate-600">{error ?? "No account profile is available."}</p>
        <Button className="mt-6" href="/auth/login">Sign in</Button>
      </Card>
    );
  }

  return <AccountProfileView profile={profile} />;
}

export function AccountProfileView({ profile }: { profile: AccountProfile }) {
  const org = profile.primaryOrganization;
  const membership = profile.primaryMembership;
  const orgLocation = [org?.city, org?.state].filter(Boolean).join(", ") || "Location pending";

  return (
    <div className="space-y-5">
      <Card className="p-8">
        <p className="text-xs font-black uppercase tracking-[.28em] text-slate-500">Account</p>
        <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-4xl font-black">User profile</h1>
            <p className="mt-3 text-slate-600">{profile.email}</p>
          </div>
          <StatusBadge status={profile.emailVerified ? "ready" : "needs_review"} />
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <ProfileMetric label="User status" value={profile.status} />
          <ProfileMetric label="Role" value={membership?.role ?? "No active role"} />
          <ProfileMetric label="Verification" value={profile.verificationStatus ?? org?.verificationStatus ?? "pending_verification"} />
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-[1.2fr_.8fr]">
        <Card className="p-6">
          <h2 className="text-2xl font-black">Organization</h2>
          <dl className="mt-5 grid gap-4 text-sm">
            <ProfileRow label="Legal name" value={org?.legalName ?? "No organization linked"} />
            <ProfileRow label="Organization ID" value={org?.orgId ?? "—"} />
            <ProfileRow label="Primary contact" value={org?.primaryContactEmail ?? profile.email} />
            <ProfileRow label="Location" value={orgLocation} />
          </dl>
        </Card>

        <Card className="p-6">
          <h2 className="text-2xl font-black">Permissions</h2>
          {profile.permissions.length > 0 ? (
            <ul className="mt-5 grid gap-2">
              {profile.permissions.map(permission => <li className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700" key={permission}>{permission}</li>)}
            </ul>
          ) : (
            <p className="mt-5 text-slate-600">No permissions assigned yet.</p>
          )}
        </Card>
      </div>
    </div>
  );
}

function ProfileMetric({ label, value }: { label: string; value: string }) {
  return <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><span className="block text-xs font-black uppercase tracking-widest text-slate-500">{label}</span><strong className="mt-2 block capitalize text-slate-950">{value.replaceAll("_", " ")}</strong></div>;
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return <div className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-slate-50 p-3 md:flex-row md:items-center md:justify-between"><dt className="font-semibold text-slate-500">{label}</dt><dd className="font-semibold text-slate-950">{value}</dd></div>;
}

export function AdminPage() { return <Placeholder title="Admin moderation" />; }
function Placeholder({ title }: { title: string }) { return <section className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm "><p className="text-xs font-black uppercase tracking-[.28em] text-slate-500">Production surface</p><h1 className="mt-3 text-4xl font-black">{title}</h1><p className="mt-4 text-slate-600">Surface pending in the production buildout task plan.</p></section>; }
